import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import AiJobProgress from '../components/AiJobProgress';
import { cancelAiJob, findActiveBatchJob, waitForAiJob, type AiJob } from '../services/aiJobs';
import '../styles/admin-common.css';
import './QuestionManagementPage.css';
import { formatDateTime } from '../utils/format';
import QualityReviewPanel, { QUALITY_ISSUE_LABELS, type QualityReview } from '../components/QualityReviewPanel';

interface Question {
  question_id: string;
  question_text: string;
  /** explanation: 보기별 해설 (보기에 붙어 다님) */
  options: Array<{ option_number: number; option_text: string; explanation?: string }>;
  /** 공식 문서 등 참고 자료 */
  references?: Array<{ url: string; title?: string }>;
  /** 저장된 해설 형식: unparsed면 구 형식 근거(reasoning)를 보기별로 옮겨야 함 */
  content_format?: 'structured' | 'convertible' | 'unparsed' | 'empty';
  correct_answers: number[];
  answer_num?: unknown;
  explanation: string;
  reasoning: string;
  keywords?: string[];
  validated?: boolean;
  validation_status?: 'pending' | 'processing' | 'ai_passed' | 'review_required' | 'validated';
  validation_issues?: Array<{ code: string; severity: 'error' | 'warning'; message: string }>;
  validation_checked_at?: string;
  domain?: string | null;
  difficulty?: 1 | 2 | 3 | null;
  domain_source?: 'ai' | 'manual';
  approved_by?: string;
  approved_at?: string;
  updated_at?: string;
  revisions?: QuestionRevision[];
  quality_review?: QualityReview;
}

type RevisionField = 'question_text' | 'options' | 'correct_answers' | 'explanation' | 'reasoning' | 'keywords' | 'domain' | 'difficulty';

interface QuestionRevision {
  at: string;
  source: string;
  by?: string;
  before: Partial<Pick<Question, RevisionField>>;
}

interface ExamDomain {
  id: string;
  name: string;
  weight: number;
}

interface DomainStat {
  domain: string;
  name: string;
  weight: number;
  total: number;
  validated: number;
  aiDraft: number;
  byDifficulty: number[];
}

interface DomainStats {
  total: number;
  pendingApproval: number;
  answerMismatch: number;
  qualityPending?: number;
  qualityUnchecked?: number;
  stats: DomainStat[];
}

const DIFFICULTY_LABELS: Record<number, string> = { 1: '쉬움', 2: '보통', 3: '어려움' };

const REVISION_SOURCE_LABELS: Record<string, string> = {
  'admin-edit': '직접 수정',
  'ai-bulk': 'AI 일괄 분석',
  'ai-validate': 'AI 검증',
  'ai-classify': 'AI 영역 분류',
  'ai-quality': 'AI 품질 수정안 적용',
  restore: '되돌리기',
  approve: '승인',
  system: '시스템',
};

const REVISION_FIELD_LABELS: Record<string, string> = {
  question_text: '문제',
  options: '선택지',
  correct_answers: '정답',
  explanation: '해설',
  reasoning: '근거',
  keywords: '키워드',
  domain: '영역',
  difficulty: '난이도',
};

const hasAnswerMismatch = (question: Question) =>
  (question.validation_issues || []).some((issue) => issue.code === 'AI_ANSWER_MISMATCH');

/** 검수 단계 배지: 승인됨 / AI 통과(승인 대기) / 검토 필요 / 대기 */
const ReviewBadge: React.FC<{ question: Question }> = ({ question }) => {
  if (question.validated) return <span className="admin-badge success">승인됨</span>;
  if (question.validation_status === 'ai_passed') return <span className="admin-badge info">AI 통과</span>;
  if (question.validation_status === 'review_required') return <span className="admin-badge warning">검토 필요</span>;
  return <span className="admin-badge secondary">대기</span>;
};

interface JsonImportPreview {
  examType: string;
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
  items: Array<Question & { import_index: number; source_url?: string }>;
  issues: Array<{ index: number; severity: 'error' | 'warning'; message: string }>;
}

interface QuestionManagementPageProps {
  embedded?: boolean;
  initialExamType?: string;
  initialQuestionId?: string;
}

const normalizeCorrectAnswers = (value: unknown): number[] => {
  const values: unknown[] = [];
  const collect = (item: unknown) => {
    if (Array.isArray(item)) {
      item.forEach(collect);
      return;
    }

    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (!trimmed) return;

      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          collect(JSON.parse(trimmed));
          return;
        } catch {
          // 쉼표로 구분된 레거시 정답 형식으로 계속 처리합니다.
        }
      }

      trimmed.split(/[,;|\s]+/).forEach((token) => values.push(token));
      return;
    }

    if (item && typeof item === 'object') {
      const dynamoItem = item as { N?: unknown; L?: unknown };
      if (dynamoItem.N !== undefined) {
        collect(dynamoItem.N);
        return;
      }
      if (dynamoItem.L !== undefined) {
        collect(dynamoItem.L);
        return;
      }
    }

    values.push(item);
  };

  collect(value);

  return Array.from(
    new Set(
      values
        .map((item) => (typeof item === 'number' ? item : Number(item)))
        .filter((item) => Number.isInteger(item) && item > 0),
    ),
  ).sort((left, right) => left - right);
};

const hasInvalidOptionNumbers = (options: Question['options']): boolean => {
  const optionNumbers = options.map((option) => option.option_number);
  return (
    new Set(optionNumbers).size !== optionNumbers.length ||
    optionNumbers.some(
      (optionNumber) => !Number.isInteger(optionNumber) || optionNumber < 1 || optionNumber > options.length,
    )
  );
};

const normalizeQuestionOptions = (question: Question): Question => {
  const hasInvalidOptionNumber = hasInvalidOptionNumbers(question.options);
  const storedAnswers = normalizeCorrectAnswers(question.correct_answers);
  const correctAnswers = storedAnswers.length > 0
    ? storedAnswers
    : normalizeCorrectAnswers(question.answer_num);

  if (!hasInvalidOptionNumber) {
    return {
      ...question,
      correct_answers: correctAnswers,
    };
  }

  const remainingAnswers = new Set(correctAnswers);
  const reorderedCorrectAnswers = question.options.reduce<number[]>((answers, option, index) => {
    if (remainingAnswers.delete(option.option_number)) {
      answers.push(index + 1);
    }
    return answers;
  }, []);

  return {
    ...question,
    options: question.options.map((option, index) => ({
      ...option,
      option_number: index + 1,
    })),
    correct_answers: reorderedCorrectAnswers,
  };
};

const QUESTIONS_PER_PAGE = 20;

const QuestionManagementPage: React.FC<QuestionManagementPageProps> = ({
  embedded = false,
  initialExamType,
  initialQuestionId,
}) => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedExamType, setSelectedExamType] = useState(initialExamType || '');
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [creatingQuestion, setCreatingQuestion] = useState<Question | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [examTypes, setExamTypes] = useState<any[]>([]);
  const [questionSearchId, setQuestionSearchId] = useState(initialQuestionId || '');
  const initialQuestionOpenedRef = React.useRef(false);
  const [optionCountFilter, setOptionCountFilter] = useState('all');
  const [validationFilter, setValidationFilter] = useState('all');
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [aiApplyError, setAiApplyError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingQuestionId, setAnalyzingQuestionId] = useState<string | null>(null);
  const [batchJob, setBatchJob] = useState<AiJob | null>(null);
  const batchWatchRef = React.useRef<AbortController | null>(null);
  const batchRunning = Boolean(batchJob && (batchJob.status === 'queued' || batchJob.status === 'running'));
  const [aiBatchLimit, setAiBatchLimit] = useState(50);
  const [questionModalError, setQuestionModalError] = useState('');
  const [pendingDeleteQuestionId, setPendingDeleteQuestionId] = useState<string | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationPreview, setValidationPreview] = useState<{ total: number; pending: number } | null>(null);
  const [validationLimit, setValidationLimit] = useState(50);
  const [validationAcknowledged, setValidationAcknowledged] = useState(false);
  const [validationConfirmationText, setValidationConfirmationText] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isValidationResetModalOpen, setIsValidationResetModalOpen] = useState(false);
  const [validationResetAcknowledged, setValidationResetAcknowledged] = useState(false);
  const [validationResetConfirmationText, setValidationResetConfirmationText] = useState('');
  const [validationResetError, setValidationResetError] = useState('');
  const [jsonImportPreview, setJsonImportPreview] = useState<JsonImportPreview | null>(null);
  const [isJsonImporting, setIsJsonImporting] = useState(false);
  const [jsonImportError, setJsonImportError] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [examDomains, setExamDomains] = useState<ExamDomain[]>([]);
  const [domainStats, setDomainStats] = useState<DomainStats | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isApproving, setIsApproving] = useState(false);
  const [reviewNotice, setReviewNotice] = useState('');
  const [classifyScope, setClassifyScope] = useState<'unassigned' | 'ai' | 'all'>('unassigned');
  const [qualityScope, setQualityScope] = useState<'all' | 'unchecked' | 'flagged'>('all');
  const [qualityBusy, setQualityBusy] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalQuestions / QUESTIONS_PER_PAGE)), [totalQuestions]);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadQuestions = async (searchId = questionSearchId, page = currentPage) => {
    if (!selectedExamType) return;

    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = new URLSearchParams({ examType: selectedExamType });

      if (searchId.trim()) {
        params.set('questionId', searchId.trim());
      } else {
        params.set('limit', String(QUESTIONS_PER_PAGE));
        params.set('offset', String((page - 1) * QUESTIONS_PER_PAGE));
        if (optionCountFilter !== 'all') params.set('optionCount', optionCountFilter);
        if (validationFilter === 'true' || validationFilter === 'false') params.set('validated', validationFilter);
        if (validationFilter === 'ai_passed') {
          params.set('status', 'ai_passed');
          params.set('validated', 'false');
        }
        if (validationFilter === 'review_required') params.set('status', 'review_required');
        if (validationFilter === 'mismatch') params.set('issue', 'AI_ANSWER_MISMATCH');
        if (validationFilter === 'quality') params.set('quality', 'needs_review');
        if (validationFilter === 'quality_unchecked') params.set('quality', 'unchecked');
        if (domainFilter !== 'all') params.set('domain', domainFilter);
      }

      const response = await apiCall(`/admin/questions?${params.toString()}`);
      if (response.ok) {
        const result = await response.json();
        const loadedQuestions = (result.data.questions || []).map((question: Question) => {
          const correctAnswers = normalizeCorrectAnswers(question.correct_answers);
          return {
            ...question,
            correct_answers: correctAnswers.length > 0
              ? correctAnswers
              : normalizeCorrectAnswers(question.answer_num),
          };
        });
        setQuestions(loadedQuestions);
        setTotalQuestions(result.data.pagination?.total || loadedQuestions.length || 0);

        if (
          !initialQuestionOpenedRef.current &&
          initialQuestionId &&
          initialExamType === selectedExamType &&
          searchId.trim() === initialQuestionId &&
          loadedQuestions.length > 0
        ) {
          const reportQuestion = loadedQuestions[0];
          const hadInvalidOptionNumbers = hasInvalidOptionNumbers(reportQuestion.options);
          setQuestionModalError(
            hadInvalidOptionNumbers
              ? '선택지 번호가 중복되거나 올바르지 않아 다시 정렬했습니다. 정답을 확인한 뒤 저장하세요.'
              : '',
          );
          setEditingQuestion(normalizeQuestionOptions(reportQuestion));
          initialQuestionOpenedRef.current = true;
        }
      }
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      setLoading(false);
    }
  };

  /** 출제 영역 정의와 영역별 문항 현황(승인 대기·정답 불일치 수 포함) */
  const loadDomainMeta = async (examType = selectedExamType) => {
    if (!examType) return;
    try {
      const [domainsResponse, statsResponse] = await Promise.all([
        apiCall(`/admin/exam-domains/${examType}`),
        apiCall(`/admin/questions/${examType}/domain-stats`),
      ]);
      const domainsBody = await domainsResponse.json().catch(() => null);
      const statsBody = await statsResponse.json().catch(() => null);
      setExamDomains(domainsResponse.ok ? domainsBody?.data?.domains || [] : []);
      setDomainStats(statsResponse.ok ? statsBody?.data || null : null);
    } catch (error) {
      console.error('Failed to load domain stats:', error);
    }
  };

  const refreshAll = () => {
    loadQuestions();
    loadDomainMeta();
  };

  const domainName = (domainId?: string | null) =>
    examDomains.find((domain) => domain.id === domainId)?.name || domainId || '미분류';

  const approveQuestions = async (questionIds: string[]) => {
    if (questionIds.length === 0) return;
    setIsApproving(true);
    setReviewNotice('');
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/approve`, {
        method: 'POST',
        body: JSON.stringify({ questionIds }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        setReviewNotice(result?.error?.message || '승인하지 못했습니다.');
        return;
      }
      const approved = result?.data?.approved?.length ?? 0;
      const skipped = (result?.data?.skipped || []) as Array<{ reason: string }>;
      const withErrors = skipped.filter((item) => item.reason === 'HAS_ERRORS').length;
      setReviewNotice(
        `${approved}개 문항을 승인했습니다.` +
        (withErrors ? ` 구조 오류가 남은 ${withErrors}개는 승인하지 않았습니다. 수정 후 다시 승인하세요.` : '') +
        (skipped.length - withErrors > 0 ? ` 찾을 수 없는 문항 ${skipped.length - withErrors}개는 건너뛰었습니다.` : ''),
      );
      refreshAll();
    } catch (error) {
      console.error('Failed to approve questions:', error);
      setReviewNotice('승인 중 네트워크 오류가 발생했습니다.');
    } finally {
      setIsApproving(false);
    }
  };

  const toggleSelected = (questionId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  const allOnPageSelected = questions.length > 0 && questions.every((question) => selectedIds.has(question.question_id));
  const toggleSelectPage = () => {
    setSelectedIds(allOnPageSelected ? new Set() : new Set(questions.map((question) => question.question_id)));
  };

  const restoreRevision = async (revision: QuestionRevision) => {
    if (!editingQuestion) return;
    if (!window.confirm('이 변경 전 값으로 되돌릴까요? 지금 값도 이력에 남고, 되돌린 문항은 다시 승인 대기가 됩니다.')) return;
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/${editingQuestion.question_id}/restore`, {
        method: 'POST',
        body: JSON.stringify({ revisionAt: revision.at }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.data) {
        setQuestionModalError(result?.error?.message || '되돌리지 못했습니다.');
        return;
      }
      const restored = result.data as Question;
      setEditingQuestion(normalizeQuestionOptions({ ...restored, correct_answers: normalizeCorrectAnswers(restored.correct_answers) }));
      setQuestionModalError('');
      refreshAll();
    } catch (error) {
      console.error('Failed to restore revision:', error);
      setQuestionModalError('되돌리는 중 네트워크 오류가 발생했습니다.');
    }
  };

  /** AI 품질 전수 검토 시작. questionIds를 주면 그 문항만 검토합니다. */
  const startQualityReview = async (questionIds?: string[]) => {
    if (!selectedExamType) return null;
    if (!questionIds) {
      const scopeText = { all: '출제 중인 문항을 포함한 전체', unchecked: '아직 검토하지 않았거나 검토 후 수정된', flagged: '검토 필요로 남아 있는' }[qualityScope];
      const countText = aiBatchLimit === 0 ? '모든' : `최대 ${aiBatchLimit}개`;
      if (!window.confirm(`${scopeText} 문항 ${countText}를 AI로 품질 검토할까요?\n의도 불명확·영어 표기·오탈자·URL을 확인하고, 문제가 있으면 '품질 검토 필요'로 표시하고 수정안을 만듭니다.\n문항 내용은 사람이 [적용]하기 전까지 바뀌지 않고, 출제도 그대로 유지됩니다. (문항당 20~40초)`)) return null;
    }
    setIsAnalyzing(true);
    try {
      const response = await apiCall('/admin/ai/quality-review', {
        method: 'POST',
        body: JSON.stringify({ examType: selectedExamType, scope: qualityScope, limit: aiBatchLimit, ...(questionIds ? { questionIds } : {}) }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        alert(result?.error?.message || 'AI 품질 검토를 시작하지 못했습니다.');
        return null;
      }
      if (!result.data?.jobId) {
        alert(result.data?.message || '검토할 문항이 없습니다.');
        return null;
      }
      const job: AiJob = { job_id: result.data.jobId, type: 'quality-review', status: 'queued', total: result.data.total, counts: { processed: 0, succeeded: 0, failed: 0 }, created_at: '', updated_at: '' };
      if (!questionIds) watchBatchJob(job);
      return job;
    } catch (error) {
      console.error('Failed to start quality review:', error);
      alert('AI 품질 검토 중 오류가 발생했습니다.');
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const reloadEditingQuestion = async (questionId: string) => {
    const response = await apiCall(`/admin/questions?examType=${encodeURIComponent(selectedExamType)}&questionId=${encodeURIComponent(questionId)}`);
    const result = await response.json().catch(() => null);
    const loaded = result?.data?.questions?.[0] as Question | undefined;
    if (loaded) setEditingQuestion(normalizeQuestionOptions({ ...loaded, correct_answers: normalizeCorrectAnswers(loaded.correct_answers) }));
  };

  const resolveQuality = async (action: 'apply' | 'dismiss', fields?: string[]) => {
    if (!editingQuestion) return;
    setQualityBusy(true);
    setQuestionModalError('');
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/${editingQuestion.question_id}/quality-review/${action}`, {
        method: 'POST',
        body: JSON.stringify(action === 'apply' ? { fields } : {}),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.data) {
        setQuestionModalError(result?.error?.message || '처리하지 못했습니다.');
        return;
      }
      const updated = result.data as Question;
      setEditingQuestion(normalizeQuestionOptions({ ...updated, correct_answers: normalizeCorrectAnswers(updated.correct_answers) }));
      refreshAll();
    } catch (error) {
      console.error('Failed to resolve quality review:', error);
      setQuestionModalError('네트워크 오류가 발생했습니다.');
    } finally {
      setQualityBusy(false);
    }
  };

  const recheckEditingQuestion = async () => {
    if (!editingQuestion) return;
    const questionId = editingQuestion.question_id;
    setQualityBusy(true);
    try {
      const job = await startQualityReview([questionId]);
      if (!job) return;
      const finished = await waitForAiJob(apiCall, job.job_id, { intervalMs: 4000 });
      if (finished.status !== 'succeeded' || (finished.counts?.failed ?? 0) > 0) {
        setQuestionModalError('AI 품질 검토에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      }
      await reloadEditingQuestion(questionId);
      refreshAll();
    } finally {
      setQualityBusy(false);
    }
  };

  const classifyDomains = async () => {
    if (!selectedExamType) return;
    const scopeText = { unassigned: '미분류 문항', ai: 'AI가 분류한 문항(재분류)', all: '직접 지정한 문항을 뺀 전체' }[classifyScope];
    const countText = aiBatchLimit === 0 ? '전체' : `최대 ${aiBatchLimit}개`;
    if (!window.confirm(`${scopeText} ${countText}의 출제 영역·난이도를 AI로 초안 분류할까요? 직접 지정한 값은 바뀌지 않고, 결과는 'AI 초안'으로 표시됩니다.`)) return;

    setIsAnalyzing(true);
    try {
      const response = await apiCall('/admin/ai/classify-domains', {
        method: 'POST',
        body: JSON.stringify({ examType: selectedExamType, scope: classifyScope, limit: aiBatchLimit }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        alert(result?.error?.message || 'AI 영역 분류를 시작하지 못했습니다.');
      } else if (!result.data?.jobId) {
        alert(result.data?.message || '분류할 문항이 없습니다.');
      } else {
        watchBatchJob({ job_id: result.data.jobId, type: 'classify-domain', status: 'queued', total: result.data.total, counts: { processed: 0, succeeded: 0, failed: 0 }, created_at: '', updated_at: '' });
      }
    } catch (error) {
      console.error('Failed to classify domains:', error);
      alert('AI 영역 분류 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadExamTypes = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/admin/exam-types');
      if (response.ok) {
        const result = await response.json();
        const types = result.data || [];
        setExamTypes(types);
        const preferredExamType = initialExamType && types.some((type: { exam_type_id: string }) => type.exam_type_id === initialExamType)
          ? initialExamType
          : types[0]?.exam_type_id || '';
        if (preferredExamType && preferredExamType !== selectedExamType) {
          setSelectedExamType(preferredExamType);
        }
      }
    } catch (error) {
      console.error('Failed to load exam types:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchQuestions = async () => {
    setCurrentPage(1);
    await loadQuestions(questionSearchId, 1);
  };

  const resetQuestionSearch = async () => {
    setQuestionSearchId('');
    setCurrentPage(1);
    await loadQuestions('', 1);
  };

  const updateQuestion = async (question: Question): Promise<boolean> => {
    // 이력은 서버가 관리하므로 보내지 않습니다. AI 초안 영역을 '확정'했다면 confirm_domain으로 알립니다.
    const { revisions: _revisions, ...rest } = question;
    void _revisions;
    const normalizedQuestion = {
      ...rest,
      correct_answers: normalizeCorrectAnswers(question.correct_answers),
      confirm_domain: question.domain_source === 'manual',
    };
    if (normalizedQuestion.correct_answers.length === 0) {
      setQuestionModalError('정답을 하나 이상 선택하세요.');
      return false;
    }

    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/${question.question_id}`, {
        method: 'PUT',
        body: JSON.stringify(normalizedQuestion),
      });
      if (response.ok) {
        closeQuestionModal();
        refreshAll();
        return true;
      }

      const errorResult = await response.json().catch(() => null);
      setQuestionModalError(errorResult?.error?.message || '문제 저장에 실패했습니다.');
      return false;
    } catch (error) {
      console.error('Failed to update question:', error);
      setQuestionModalError('문제 저장 중 네트워크 오류가 발생했습니다.');
      return false;
    }
  };

  const createQuestion = async (question: Question) => {
    const response = await apiCall(`/admin/questions/${selectedExamType}`, {
      method: 'POST',
      body: JSON.stringify(question),
    });
    if (response.ok) {
      closeQuestionModal();
      loadQuestions();
    }
  };

  const deleteQuestion = async (questionId: string) => {
    const response = await apiCall(`/admin/questions/${selectedExamType}/${questionId}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      loadQuestions();
    }
  };

  const openCreateQuestionModal = () => {
    setQuestionModalError('');
    setCreatingQuestion({
      question_id: '',
      question_text: '',
      options: [
        { option_number: 1, option_text: '' },
        { option_number: 2, option_text: '' },
        { option_number: 3, option_text: '' },
        { option_number: 4, option_text: '' },
      ],
      correct_answers: [],
      explanation: '',
      reasoning: '',
      keywords: [],
      validated: false,
    });
  };

  const openEditQuestionModal = (question: Question) => {
    const normalizedQuestion = normalizeQuestionOptions(question);
    const hadInvalidOptionNumbers = hasInvalidOptionNumbers(question.options);

    setAiAnalysis(null);
    setQuestionModalError(
      hadInvalidOptionNumbers
        ? '선택지 번호가 중복되거나 올바르지 않아 다시 정렬했습니다. 정답을 확인한 뒤 저장하세요.'
        : '',
    );
    setEditingQuestion(normalizedQuestion);
  };

  const closeQuestionModal = () => {
    setQuestionModalError('');
    setEditingQuestion(null);
    setCreatingQuestion(null);
  };

  const validateQuestionForSave = (question: Question) => {
    if (!question.question_text.trim()) return '문제 내용을 입력하세요.';
    if (!question.options.length || question.options.some(option => !option.option_text.trim())) return '모든 선택지를 입력하세요.';
    if (question.correct_answers.length === 0) return '정답을 하나 이상 선택하세요.';
    return '';
  };

  const toggleValidation = async (questionId: string, validated: boolean) => {
    const response = await apiCall(`/admin/questions/${selectedExamType}/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify({ validated }),
    });
    if (response.ok) {
      refreshAll();
    }
  };

  const analyzeQuestionWithAI = async (question: Question) => {
    setAiApplyError('');
    setIsAnalyzing(true);
    setAnalyzingQuestionId(question.question_id);
    try {
      const response = await apiCall('/admin/ai/analyze-question', {
        method: 'POST',
        body: JSON.stringify({
          examType: selectedExamType,
          questionId: question.question_id,
          questionText: question.question_text,
          options: question.options,
          currentAnswers: question.correct_answers,
          explanation: question.explanation,
          reasoning: question.reasoning,
        }),
      });

      const started = await response.json().catch(() => null);
      if (!response.ok || !started?.data?.jobId) {
        alert(started?.error?.message || 'AI 분석을 시작하지 못했습니다.');
        return;
      }

      // 분석은 백그라운드 작업으로 실행되고, 끝날 때까지 상태를 확인합니다 (보통 30초~1분).
      const job = await waitForAiJob<Record<string, unknown>>(apiCall, started.data.jobId);
      if (job.status === 'succeeded' && job.result) {
        setAiAnalysis({ questionId: question.question_id, ...job.result });
      } else {
        alert(job.error ? `AI 분석에 실패했습니다: ${job.error}` : 'AI 분석에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to analyze question:', error);
      alert('AI 분석 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
      setAnalyzingQuestionId(null);
    }
  };

  /** 일괄 작업 진행 상황을 따라가다가 끝나면 목록을 새로 고칩니다. */
  const watchBatchJob = (job: AiJob) => {
    batchWatchRef.current?.abort();
    const controller = new AbortController();
    batchWatchRef.current = controller;
    setBatchJob(job);
    waitForAiJob(apiCall, job.job_id, { onProgress: setBatchJob, intervalMs: 5000, signal: controller.signal })
      .then(() => refreshAll())
      .catch((error) => {
        if ((error as Error).name !== 'AbortError') console.error('AI 작업 상태 확인 실패:', error);
      });
  };

  const cancelBatchJob = async () => {
    if (!batchJob || !window.confirm('진행 중인 AI 작업을 취소할까요? 지금 처리 중인 문항까지만 저장됩니다.')) return;
    await cancelAiJob(apiCall, batchJob.job_id);
  };

  const applyAiAnalysis = async () => {
    const question = questions.find((item) => item.question_id === aiAnalysis?.questionId);
    const hasCompleteAnalysis =
      aiAnalysis?.explanation && aiAnalysis.explanation !== '설명을 찾을 수 없습니다.' &&
      aiAnalysis?.optionAnalysis && aiAnalysis.optionAnalysis !== '선택지 분석을 찾을 수 없습니다.';

    if (!question || !hasCompleteAnalysis) {
      setAiApplyError('AI 응답이 완성되지 않았거나 원본 문제를 찾을 수 없습니다. AI 분석을 다시 실행하세요.');
      return;
    }

    // 정답은 바꾸지 않습니다 (AI 추천 정답은 참고만). 키워드는 AI가 만든 게 있을 때만 바꿉니다.
    // 선택지별 분석(reasoning)은 서버가 저장할 때 보기별 해설로 나눕니다.
    const translatedOptions = Array.isArray(aiAnalysis.translatedOptions) && aiAnalysis.translatedOptions.length === question.options.length
      ? (aiAnalysis.translatedOptions as Array<{ option_number: number; option_text: string }>).map(({ option_number, option_text }) => ({ option_number, option_text }))
      : undefined;
    const updates = {
      explanation: aiAnalysis.explanation,
      reasoning: aiAnalysis.optionAnalysis,
      ...(Array.isArray(aiAnalysis.keywords) && aiAnalysis.keywords.length > 0 ? { keywords: aiAnalysis.keywords } : {}),
      ...(aiAnalysis.translatedQuestionText ? { question_text: aiAnalysis.translatedQuestionText } : {}),
      ...(translatedOptions ? { options: translatedOptions } : {}),
      ...(Array.isArray(aiAnalysis.references) && aiAnalysis.references.length > 0
        ? { references: [...(question.references || []), ...aiAnalysis.references.filter((reference: { url: string }) => !(question.references || []).some((item) => item.url === reference.url))] }
        : {}),
    };

    setAiApplyError('');
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/${question.question_id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        setAiApplyError(result?.error?.message || `문제 저장에 실패했습니다. (HTTP ${response.status})`);
        return;
      }

      setAiAnalysis(null);
      await loadQuestions();
    } catch (error) {
      console.error('Failed to apply AI analysis:', error);
      setAiApplyError('AI 분석 결과 저장 중 네트워크 오류가 발생했습니다.');
    }
  };

  const handleJsonImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedExamType) return;
    setJsonImportError('');
    setJsonImportPreview(null);
    setIsJsonImporting(true);
    try {
      const parsed = JSON.parse(await file.text()) as { records?: unknown };
      const response = await apiCall(`/admin/questions/${selectedExamType}/import-json/preview`, {
        method: 'POST',
        body: JSON.stringify({ records: parsed.records }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error?.message || 'JSON 미리보기에 실패했습니다.');
      setJsonImportPreview(result.data);
    } catch (error) {
      setJsonImportError(error instanceof Error ? error.message : 'JSON 파일을 읽지 못했습니다.');
    } finally {
      setIsJsonImporting(false);
    }
  };

  const importJsonQuestions = async () => {
    if (!jsonImportPreview || jsonImportPreview.invalid > 0 || jsonImportPreview.valid === 0) return;
    if (!window.confirm(`정상 문항 ${jsonImportPreview.valid}개를 ${selectedExamType} 문제은행에 추가하시겠습니까? 기존 중복 ID는 건너뜁니다.`)) return;
    setIsJsonImporting(true);
    setJsonImportError('');
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/import-json`, {
        method: 'POST',
        body: JSON.stringify({ items: jsonImportPreview.items, confirmed: true }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error?.message || 'JSON 문제 저장에 실패했습니다.');
      setJsonImportPreview(null);
      alert(`JSON 문제 가져오기 완료\n추가: ${result.data.imported}개\n건너뜀: ${result.data.skipped}개`);
      await loadQuestions();
    } catch (error) {
      setJsonImportError(error instanceof Error ? error.message : 'JSON 문제 저장 중 오류가 발생했습니다.');
    } finally {
      setIsJsonImporting(false);
    }
  };

  const openValidationModal = async () => {
    if (!selectedExamType) return;
    setValidationError('');
    setValidationAcknowledged(false);
    setValidationConfirmationText('');
    setValidationPreview(null);
    setIsValidationModalOpen(true);
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/validation-scan`, { method: 'POST' });
      if (!response.ok) throw new Error('전체 문항 사전 스캔을 완료하지 못했습니다.');
      const result = await response.json();
      setValidationPreview({ total: result.data.total, pending: result.data.pending });
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : '전체 문항 사전 스캔을 완료하지 못했습니다.');
    }
  };

  const openValidationResetModal = () => {
    setValidationResetError('');
    setValidationResetAcknowledged(false);
    setValidationResetConfirmationText('');
    setIsValidationResetModalOpen(true);
  };

  const resetAllValidationStates = async () => {
    if (!selectedExamType || !validationResetAcknowledged || validationResetConfirmationText !== '전체 검수 대기') return;
    setValidationResetError('');
    setIsAnalyzing(true);
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/validation-reset`, {
        method: 'POST',
        body: JSON.stringify({ confirmed: true }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error?.message || '검수 상태 초기화에 실패했습니다.');
      }
      const result = await response.json();
      setIsValidationResetModalOpen(false);
      alert(`${result.data.reset.toLocaleString()}개 문항을 검수 대기로 초기화했습니다.`);
      await loadQuestions();
    } catch (error) {
      setValidationResetError(error instanceof Error ? error.message : '검수 상태 초기화 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const startBatchValidation = async () => {
    if (!selectedExamType || !validationAcknowledged || validationConfirmationText !== '검수 시작') return;
    setValidationError('');
    setIsAnalyzing(true);
    try {
      const response = await apiCall(`/admin/questions/${selectedExamType}/validate-batch`, {
        method: 'POST',
        body: JSON.stringify({ confirmed: true, limit: validationLimit }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error?.message || '문제 일괄 검증에 실패했습니다.');
      }
      const result = await response.json();
      setIsValidationModalOpen(false);
      if (!result.data?.jobId) {
        alert(result.data?.message || '검수 대기 문항이 없습니다.');
        return;
      }
      watchBatchJob({ job_id: result.data.jobId, type: 'validate-batch', status: 'queued', total: result.data.total, counts: { processed: 0, succeeded: 0, failed: 0 }, created_at: '', updated_at: '' });
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : '문제 일괄 검증 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const bulkAnalyze = async () => {
    if (!selectedExamType) return;
    const scopeText = aiBatchLimit === 0 ? '분석 대상 전체' : `${aiBatchLimit}개`;
    if (!window.confirm(`AI를 사용해 선택한 시험 유형의 문제를 ${scopeText} 일괄 분석하시겠습니까? 시간이 오래 걸릴 수 있습니다.`)) return;

    setIsAnalyzing(true);
    try {
      const response = await apiCall('/admin/ai/bulk-analyze', {
        method: 'POST',
        body: JSON.stringify({ examType: selectedExamType, limit: aiBatchLimit }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        alert(result?.error?.message || 'AI 일괄 분석을 시작하지 못했습니다.');
      } else if (!result.data?.jobId) {
        alert(result.data?.message || '분석할 문항이 없습니다.');
      } else {
        watchBatchJob({ job_id: result.data.jobId, type: 'bulk-analyze', status: 'queued', total: result.data.total, counts: { processed: 0, succeeded: 0, failed: 0 }, created_at: '', updated_at: '' });
      }
    } catch (error) {
      console.error('Failed to bulk analyze:', error);
      alert('AI 일괄 분석 중 오류가 발생했습니다.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    loadExamTypes();
    return () => batchWatchRef.current?.abort();
  }, []);

  // 시험 유형을 고르면 이전에 시작해 둔 일괄 작업이 아직 돌고 있는지 확인해 진행 표시를 이어 붙입니다.
  useEffect(() => {
    if (!selectedExamType) return;
    setDomainFilter('all');
    setReviewNotice('');
    loadDomainMeta(selectedExamType);
    batchWatchRef.current?.abort();
    setBatchJob(null);
    findActiveBatchJob(apiCall, selectedExamType).then((job) => {
      if (job) watchBatchJob(job);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamType]);

  useEffect(() => {
    if (selectedExamType) {
      setCurrentPage(1);
      const initialSearchId = initialExamType === selectedExamType ? initialQuestionId || '' : '';
      setQuestionSearchId(initialSearchId);
      loadQuestions(initialSearchId, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamType, optionCountFilter, validationFilter, domainFilter]);

  useEffect(() => {
    if (selectedExamType) {
      loadQuestions(questionSearchId, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  if (!user?.is_admin) {
    return (
      <div className={`admin-page ${embedded ? 'admin-page--embedded' : ''}`}>
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${embedded ? 'admin-page--embedded' : ''}`}>
      {!embedded && (
        <header className="admin-page-title">
          <span className="admin-page-title__eyebrow">문제은행</span>
          <h1>문제 관리</h1>
        </header>
      )}

      {loading && <div className="loading">불러오는 중...</div>}

      <div className="admin-section question-management-panel">
        <div className="admin-header question-management-panel__header">
          <div>
            <h2>문제 목록</h2>
            <p>문제, 선택지, 정답, 해설, 검수 상태를 관리합니다.</p>
          </div>

          <div className="admin-controls question-management-controls">
            <div className="admin-form-inline question-management-controls__group">
              <select className="admin-form-control sm select" value={selectedExamType} onChange={(event) => setSelectedExamType(event.target.value)}>
                <option value="">시험 유형 선택</option>
                {examTypes.map(type => (
                  <option key={type.exam_type_id} value={type.exam_type_id}>{type.name}</option>
                ))}
              </select>
              <select className="admin-form-control sm select" value={optionCountFilter} onChange={(event) => setOptionCountFilter(event.target.value)}>
                <option value="all">모든 선택지</option>
                <option value="4">4개 선택지</option>
                <option value="5">5개 선택지</option>
                <option value="6">6개 선택지</option>
              </select>
              <select className="admin-form-control sm select" value={validationFilter} onChange={(event) => setValidationFilter(event.target.value)}>
                <option value="all">모든 검수 상태</option>
                <option value="true">승인됨 (출제 중)</option>
                <option value="false">미승인 전체</option>
                <option value="ai_passed">AI 통과 · 승인 대기</option>
                <option value="review_required">검토 필요</option>
                <option value="mismatch">AI 정답 불일치</option>
                <option value="quality">AI 품질 검토 필요</option>
                <option value="quality_unchecked">품질 미검토</option>
              </select>
              {examDomains.length > 0 && (
                <select className="admin-form-control sm select" value={domainFilter} onChange={(event) => setDomainFilter(event.target.value)} aria-label="출제 영역">
                  <option value="all">모든 영역</option>
                  {examDomains.map((domain) => (
                    <option key={domain.id} value={domain.id}>{domain.name}</option>
                  ))}
                  <option value="unassigned">미분류</option>
                </select>
              )}
            </div>

            <div className="admin-form-inline question-management-controls__group">
              <input
                type="text"
                className="admin-form-control sm"
                placeholder="문제 ID로 검색"
                value={questionSearchId}
                onChange={(event) => setQuestionSearchId(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && searchQuestions()}
              />
              <button className="admin-btn secondary" onClick={searchQuestions}>검색</button>
              <button className="admin-btn secondary" onClick={resetQuestionSearch}>전체</button>
              <button className="admin-btn primary" onClick={openCreateQuestionModal}>문제 추가</button>
              <label className="admin-btn secondary json-import-button">
                JSON 업로드
                <input type="file" accept="application/json,.json" onChange={handleJsonImportFile} disabled={isJsonImporting || !selectedExamType} />
              </label>
            </div>
          </div>
        </div>

        {selectedExamType && (
          <div className="bulk-actions">
            <button className="admin-btn danger" onClick={openValidationResetModal} disabled={isAnalyzing || batchRunning}>
              전체 검수 대기 전환
            </button>
            <button className="admin-btn warning" onClick={openValidationModal} disabled={isAnalyzing || batchRunning}>
              문제 일괄 검증
            </button>
            <label className="ai-batch-limit-control">
              <span>AI 처리 수</span>
              <select className="admin-form-control sm select" value={aiBatchLimit} onChange={(event) => setAiBatchLimit(Number(event.target.value))} disabled={isAnalyzing || batchRunning}>
                <option value={0}>전체</option>
                <option value={25}>25개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
                <option value={200}>200개</option>
              </select>
            </label>
            <button className="admin-btn secondary" onClick={bulkAnalyze} disabled={isAnalyzing || batchRunning}>
              {batchRunning ? 'AI 작업 진행 중...' : 'AI 일괄 분석'}
            </button>
            <label className="ai-batch-limit-control">
              <span>품질 검토 범위</span>
              <select
                className="admin-form-control sm select"
                value={qualityScope}
                onChange={(event) => setQualityScope(event.target.value as 'all' | 'unchecked' | 'flagged')}
                disabled={isAnalyzing || batchRunning}
              >
                <option value="all">전체 (출제 중 포함)</option>
                <option value="unchecked">미검토·검토 후 수정됨</option>
                <option value="flagged">검토 필요 재검토</option>
              </select>
            </label>
            <button className="admin-btn primary" onClick={() => startQualityReview()} disabled={isAnalyzing || batchRunning}>
              AI 품질 전수 검토
            </button>
          </div>
        )}

        {batchJob && (
          <AiJobProgress job={batchJob} onCancel={cancelBatchJob} onDismiss={() => setBatchJob(null)} />
        )}

        {selectedExamType && domainStats && (() => {
          const unassigned = domainStats.stats.find((stat) => stat.domain === 'unassigned')?.total ?? 0;
          const domainRows = domainStats.stats.filter((stat) => stat.domain !== 'unassigned');
          const classifiedTotal = domainRows.reduce((sum, stat) => sum + stat.total, 0);
          return (
            <section className="qbank-overview" aria-label="문제은행 검수·영역 현황">
              <div className="qbank-overview__chips">
                <button
                  type="button"
                  className={`qbank-chip ${validationFilter === 'ai_passed' ? 'is-active' : ''}`}
                  onClick={() => setValidationFilter(validationFilter === 'ai_passed' ? 'all' : 'ai_passed')}
                >
                  <span>승인 대기</span>
                  <strong className="tabular">{domainStats.pendingApproval.toLocaleString()}</strong>
                </button>
                <button
                  type="button"
                  className={`qbank-chip qbank-chip--danger ${validationFilter === 'quality' ? 'is-active' : ''}`}
                  onClick={() => setValidationFilter(validationFilter === 'quality' ? 'all' : 'quality')}
                  title="AI 품질 검토에서 의도 불명확·영어 표기·오탈자·URL 문제가 발견된 문항"
                >
                  <span>품질 검토 필요</span>
                  <strong className="tabular">{(domainStats.qualityPending ?? 0).toLocaleString()}</strong>
                </button>
                {(domainStats.qualityUnchecked ?? 0) > 0 && (
                  <button
                    type="button"
                    className={`qbank-chip ${validationFilter === 'quality_unchecked' ? 'is-active' : ''}`}
                    onClick={() => setValidationFilter(validationFilter === 'quality_unchecked' ? 'all' : 'quality_unchecked')}
                  >
                    <span>품질 미검토</span>
                    <strong className="tabular">{(domainStats.qualityUnchecked ?? 0).toLocaleString()}</strong>
                  </button>
                )}
                <button
                  type="button"
                  className={`qbank-chip qbank-chip--danger ${validationFilter === 'mismatch' ? 'is-active' : ''}`}
                  onClick={() => setValidationFilter(validationFilter === 'mismatch' ? 'all' : 'mismatch')}
                >
                  <span>AI 정답 불일치</span>
                  <strong className="tabular">{domainStats.answerMismatch.toLocaleString()}</strong>
                </button>
                {examDomains.length > 0 && (
                  <button
                    type="button"
                    className={`qbank-chip ${domainFilter === 'unassigned' ? 'is-active' : ''}`}
                    onClick={() => setDomainFilter(domainFilter === 'unassigned' ? 'all' : 'unassigned')}
                  >
                    <span>영역 미분류</span>
                    <strong className="tabular">{unassigned.toLocaleString()}</strong>
                  </button>
                )}
                {examDomains.length > 0 && (
                  <div className="qbank-overview__classify">
                    <select
                      className="admin-form-control sm select"
                      value={classifyScope}
                      onChange={(event) => setClassifyScope(event.target.value as 'unassigned' | 'ai' | 'all')}
                      disabled={isAnalyzing || batchRunning}
                      aria-label="AI 영역 분류 범위"
                    >
                      <option value="unassigned">미분류만</option>
                      <option value="ai">AI 초안 재분류</option>
                      <option value="all">직접 지정 제외 전체</option>
                    </select>
                    <button type="button" className="admin-btn sm secondary" onClick={classifyDomains} disabled={isAnalyzing || batchRunning}>
                      AI 영역 분류
                    </button>
                  </div>
                )}
              </div>

              {domainRows.length > 0 && (
                <div className="qbank-domains">
                  {domainRows.map((stat) => {
                    const share = classifiedTotal ? Math.round((stat.total / classifiedTotal) * 100) : 0;
                    const gap = share - stat.weight;
                    const showGap = classifiedTotal >= 20 && Math.abs(gap) >= 5;
                    return (
                      <button
                        type="button"
                        key={stat.domain}
                        className={`qbank-domain ${domainFilter === stat.domain ? 'is-active' : ''}`}
                        onClick={() => setDomainFilter(domainFilter === stat.domain ? 'all' : stat.domain)}
                        title="이 영역 문항만 보기"
                      >
                        <span className="qbank-domain__head">
                          <span className="qbank-domain__name">{stat.name}</span>
                          {showGap && (
                            <span className={`qbank-domain__gap ${gap < 0 ? 'is-low' : 'is-high'}`}>
                              {gap < 0 ? '부족' : '과다'} {Math.abs(gap)}%p
                            </span>
                          )}
                        </span>
                        <span className="qbank-domain__bar" aria-hidden="true">
                          <span className="qbank-domain__fill" style={{ width: `${Math.min(share, 100)}%` }} />
                          <span className="qbank-domain__target" style={{ left: `${stat.weight}%` }} />
                        </span>
                        <span className="qbank-domain__meta tabular">
                          {stat.total.toLocaleString()}문항 · 승인 {stat.validated.toLocaleString()} · 비중 {share}% / 가이드 {stat.weight}%
                          {stat.aiDraft > 0 && ` · AI 초안 ${stat.aiDraft}`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })()}

        {reviewNotice && (
          <p className="qbank-notice" role="status">
            {reviewNotice}
            <button type="button" className="qbank-notice__close" onClick={() => setReviewNotice('')} aria-label="알림 닫기">닫기</button>
          </p>
        )}

        {selectedIds.size > 0 && (
          <div className="qbank-selection" role="region" aria-label="선택한 문항 작업">
            <span className="tabular">{selectedIds.size}개 선택</span>
            <button type="button" className="admin-btn sm primary" onClick={() => approveQuestions([...selectedIds])} disabled={isApproving}>
              {isApproving ? '승인 중…' : '선택 문항 승인'}
            </button>
            <button type="button" className="admin-btn sm secondary" onClick={() => setSelectedIds(new Set())}>선택 해제</button>
          </div>
        )}

        <div className="questions-stats">
          <span>총 {totalQuestions.toLocaleString()}개 문제</span>
          <span>페이지 {currentPage} / {totalPages}</span>
        </div>

        <div className="question-admin-table-wrap">
          <table className="admin-table question-admin-table qbank-table">
            <thead>
              <tr>
                <th className="col-select">
                  <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectPage} aria-label="이 페이지 문항 모두 선택" />
                </th>
                <th className="col-question-id">ID</th>
                <th className="col-question-text">문제 내용</th>
                <th className="col-option-count">선택지</th>
                <th className="col-correct-answer">정답</th>
                <th className="col-domain">영역 · 난이도</th>
                <th className="col-question-meta">자료</th>
                <th className="col-validated">검수</th>
                <th className="col-question-action">작업</th>
              </tr>
            </thead>
            <tbody>
              {questions.map(question => (
                <tr key={question.question_id} className={selectedIds.has(question.question_id) ? 'is-selected' : undefined}>
                  <td className="col-select">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(question.question_id)}
                      onChange={() => toggleSelected(question.question_id)}
                      aria-label={`문항 ${question.question_id} 선택`}
                    />
                  </td>
                  <td className="col-question-id">
                    <span className="admin-badge info question-id-badge" title={question.question_id}>
                      #{question.question_id.split('-').slice(-1)[0] || question.question_id}
                    </span>
                  </td>
                  <td className="col-question-text" title={question.question_text}>
                    <div className="question-text-cell">
                      {question.question_text}
                    </div>
                  </td>
                  <td className="col-option-count">
                    <span className="admin-badge secondary">{question.options?.length || 0}개</span>
                  </td>
                  <td className="col-correct-answer">
                    <div className="correct-answers">
                      {question.correct_answers?.map(answer => (
                        <span key={answer} className="admin-badge success">{answer}</span>
                      ))}
                    </div>
                  </td>
                  <td className="col-domain">
                    <div className="qbank-domain-cell">
                      {question.domain ? (
                        <span
                          className={`qbank-tag ${question.domain_source === 'ai' ? 'qbank-tag--draft' : ''}`}
                          title={question.domain_source === 'ai' ? 'AI 초안 — 수정 화면에서 확정하세요' : '출제관 확인'}
                        >
                          {domainName(question.domain)}
                        </span>
                      ) : (
                        <span className="qbank-tag qbank-tag--empty">미분류</span>
                      )}
                      {question.difficulty && <span className="qbank-difficulty">{DIFFICULTY_LABELS[question.difficulty]}</span>}
                    </div>
                  </td>
                  <td className="col-question-meta">
                    <div className="admin-flex admin-gap-2">
                      {question.explanation?.trim() && (
                        <span className="question-meta-icon question-meta-icon--explanation" title="해설 있음" aria-label="해설 있음">?</span>
                      )}
                      {(question.options.some((option) => option.explanation?.trim()) || question.reasoning?.trim()) && (
                        <span
                          className="question-meta-icon question-meta-icon--reasoning"
                          title={question.content_format === 'unparsed' ? '보기별 해설: 구 형식 (보기별로 옮겨야 함)' : '보기별 해설 있음'}
                          aria-label="보기별 해설 있음"
                        >
                          i
                        </span>
                      )}
                      {question.keywords && question.keywords.length > 0 && (
                        <span className="question-meta-icon question-meta-icon--keywords" title="키워드 있음" aria-label="키워드 있음">#</span>
                      )}
                    </div>
                  </td>
                  <td className="col-validated">
                    <div className="qbank-review-cell">
                      <ReviewBadge question={question} />
                      {hasAnswerMismatch(question) && <span className="admin-badge danger" title="AI가 제안한 정답이 저장된 정답과 다릅니다">정답 불일치</span>}
                      {question.quality_review?.status === 'needs_review' && (
                        <span
                          className="admin-badge warning"
                          title={question.quality_review.issues.map((issue) => `${QUALITY_ISSUE_LABELS[issue.type] || issue.type}: ${issue.message}`).join('\n')}
                        >
                          품질 검토
                        </span>
                      )}
                      {question.validated ? (
                        <button
                          className="admin-btn sm secondary"
                          onClick={() => {
                            if (window.confirm('승인을 취소하면 이 문항은 시험에서 빠집니다. 계속할까요?')) {
                              toggleValidation(question.question_id, false);
                            }
                          }}
                        >
                          승인 취소
                        </button>
                      ) : (
                        <button className="admin-btn sm primary" onClick={() => approveQuestions([question.question_id])} disabled={isApproving}>
                          승인
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="col-question-action">
                    <div className="admin-flex admin-gap-2">
                      <button className="admin-btn sm primary" onClick={() => openEditQuestionModal(question)}>수정</button>
                      <button className="admin-btn sm secondary" onClick={() => analyzeQuestionWithAI(question)} disabled={isAnalyzing} title="AI 분석은 30초~1분 걸립니다">
                        {analyzingQuestionId === question.question_id ? '분석 중…' : 'AI'}
                      </button>
                      <button className="admin-btn sm danger" onClick={() => setPendingDeleteQuestionId(question.question_id)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && questions.length === 0 && (
                <tr>
                  <td colSpan={9} className="admin-empty-cell">조건에 맞는 문제가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalQuestions > QUESTIONS_PER_PAGE && (
          <div className="admin-pagination">
            <button className="admin-btn secondary sm" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
              이전
            </button>
            <span>{currentPage} / {totalPages}</span>
            <button className="admin-btn secondary sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              다음
            </button>
          </div>
        )}
      </div>

      {aiAnalysis && (
        <div className="question-ai-modal-overlay">
          <div className="question-ai-modal">
            <div className="question-ai-modal__header">
              <h3>AI 분석 결과</h3>
              <button className="question-ai-modal__close" onClick={() => setAiAnalysis(null)} aria-label="닫기">닫기</button>
            </div>
            <div className="question-ai-modal__body">
              <div className="analysis-section">
                <h4>분석 결과</h4>
                <div className="analysis-content">
                  <div className="analysis-item"><strong>신뢰도:</strong> {aiAnalysis.confidence}%</div>
                  <div className="analysis-item">
                    <strong>AI 추천 정답 (참고용, 적용해도 정답은 바뀌지 않음):</strong>
                    <div className="recommended-answers">
                      {aiAnalysis.recommendedAnswers?.map((answer: number) => (
                        <span key={answer} className="admin-badge success">{answer}</span>
                      ))}
                    </div>
                  </div>
                  <div className="analysis-item"><strong>해설:</strong><p>{aiAnalysis.explanation || aiAnalysis.analysis}</p></div>
                  {aiAnalysis.translatedQuestionText && (
                    <div className="analysis-item analysis-item--translation">
                      <strong>한국어 문제 번역:</strong><p>{aiAnalysis.translatedQuestionText}</p>
                      {Array.isArray(aiAnalysis.translatedOptions) && aiAnalysis.translatedOptions.length > 0 && (
                        <ol>
                          {aiAnalysis.translatedOptions.map((option: { option_number: number; option_text: string }) => (
                            <li key={option.option_number}>{option.option_text}</li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                  {Array.isArray(aiAnalysis.optionExplanations) && aiAnalysis.optionExplanations.length > 0 ? (
                    <div className="analysis-item">
                      <strong>보기별 해설:</strong>
                      <ol className="qbank-ai-options">
                        {aiAnalysis.optionExplanations.map((item: { option_number: number; explanation: string }) => (
                          <li key={item.option_number}><span className="tabular">{item.option_number}.</span> {item.explanation || '(없음)'}</li>
                        ))}
                      </ol>
                    </div>
                  ) : aiAnalysis.optionAnalysis && <div className="analysis-item"><strong>근거:</strong><p>{aiAnalysis.optionAnalysis}</p></div>}
                  {aiAnalysis.suggestions && <div className="analysis-item"><strong>개선 제안:</strong><p>{aiAnalysis.suggestions}</p></div>}
                  {Array.isArray(aiAnalysis.documentationSources) && aiAnalysis.documentationSources.length > 0 && (
                    <div className="analysis-item analysis-item--sources">
                      <strong>공식 AWS 문서:</strong>
                      <ul>
                        {aiAnalysis.documentationSources.map((source: { title: string; url: string }) => (
                          <li key={source.url}>
                            <a href={source.url} target="_blank" rel="noreferrer">{source.title || source.url}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="question-ai-modal__footer">
              {aiApplyError && <p className="question-ai-modal__error" role="alert">{aiApplyError}</p>}
              <button className="admin-btn primary" onClick={applyAiAnalysis}>
                분석 결과 적용
              </button>
              <button className="admin-btn secondary" onClick={() => setAiAnalysis(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {creatingQuestion && (
        <QuestionModal
          title="문제 추가"
          question={creatingQuestion}
          domains={examDomains}
          error={questionModalError}
          onChange={setCreatingQuestion}
          onClose={closeQuestionModal}
          onSave={() => {
            const error = validateQuestionForSave(creatingQuestion);
            if (error) {
              setQuestionModalError(error);
              return;
            }
            createQuestion(creatingQuestion);
          }}
        />
      )}

      {editingQuestion && (
        <QuestionModal
          title="문제 수정"
          question={editingQuestion}
          domains={examDomains}
          onRestore={restoreRevision}
          quality={editingQuestion.quality_review ? (
            <QualityReviewPanel
              key={`${editingQuestion.question_id}-${editingQuestion.quality_review.checked_at}-${editingQuestion.quality_review.status}`}
              question={editingQuestion}
              domains={examDomains}
              busy={qualityBusy}
              onApply={(fields) => {
                if (window.confirm('선택한 수정안을 문항에 반영하고 완료 처리할까요? 지금 모달에서 고친 내용 중 저장하지 않은 것은 사라집니다.')) {
                  resolveQuality('apply', fields);
                }
              }}
              onDismiss={() => {
                if (window.confirm('AI 지적을 확인했고 문제가 없거나 직접 고쳤다고 보고 완료 처리할까요? (직접 고친 내용은 먼저 저장하세요)')) {
                  resolveQuality('dismiss');
                }
              }}
              onRecheck={recheckEditingQuestion}
            />
          ) : (
            <div className="qbank-quality-empty">
              <span>아직 AI 품질 검토를 하지 않은 문항입니다.</span>
              <button type="button" className="admin-btn sm secondary" onClick={recheckEditingQuestion} disabled={qualityBusy}>
                {qualityBusy ? '검토 중… (20~40초)' : 'AI 품질 검토'}
              </button>
            </div>
          )}
          error={questionModalError}
          onChange={setEditingQuestion}
          onClose={closeQuestionModal}
          onSave={() => {
            const error = validateQuestionForSave(editingQuestion);
            if (error) {
              setQuestionModalError(error);
              return;
            }
            updateQuestion(editingQuestion);
          }}
        />
      )}

      {jsonImportPreview && (
        <div className="question-action-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="json-import-title">
          <div className="question-action-modal json-import-modal">
            <h3 id="json-import-title">JSON 문제 가져오기 미리보기</h3>
            <p className="json-import-modal__summary">
              전체 {jsonImportPreview.total}개 · 정상 {jsonImportPreview.valid}개 · 오류 {jsonImportPreview.invalid}개 · 중복 {jsonImportPreview.duplicates}개
            </p>
            <p>정상 문항만 저장할 수 있습니다. 저장되는 문항은 검수 대기 상태로 시작하며 정답은 JSON의 `model_recommended_answer`를 사용합니다.</p>
            {jsonImportPreview.issues.length > 0 && (
              <div className="json-import-modal__issues">
                {jsonImportPreview.issues.slice(0, 20).map((issue, index) => (
                  <p key={`${issue.index}-${index}`} className={`json-import-modal__issue json-import-modal__issue--${issue.severity}`}>
                    #{issue.index + 1} · {issue.message}
                  </p>
                ))}
                {jsonImportPreview.issues.length > 20 && <p>그 외 {jsonImportPreview.issues.length - 20}개 이슈가 더 있습니다.</p>}
              </div>
            )}
            {jsonImportError && <p className="question-validation-modal__error" role="alert">{jsonImportError}</p>}
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setJsonImportPreview(null)} disabled={isJsonImporting}>취소</button>
              <button className="admin-btn primary" onClick={importJsonQuestions} disabled={isJsonImporting || jsonImportPreview.invalid > 0 || jsonImportPreview.valid === 0}>
                {isJsonImporting ? '저장 중...' : '정상 문항 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {jsonImportError && !jsonImportPreview && (
        <div className="question-validation-modal__error" role="alert">{jsonImportError}</div>
      )}

      {isValidationResetModalOpen && (
        <div className="question-action-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="validation-reset-title">
          <div className="question-action-modal question-validation-modal question-validation-modal--danger">
            <h3 id="validation-reset-title">전체 검수 대기 전환</h3>
            <p className="question-validation-modal__warning question-validation-modal__warning--danger">
              이 작업은 <strong>{selectedExamType}</strong>의 모든 문항을 검수 대기 상태로 변경합니다. 진행 중으로 남아 있는 AI 검증 상태와 기존 검수 이슈 기록도 초기화되며, 이후 문제 일괄 검증에서 모든 문항이 AI 대상이 될 수 있습니다. 문항 내용·선택지·정답·해설 자체는 변경하지 않습니다.
            </p>
            <label className="question-validation-modal__check">
              <input type="checkbox" checked={validationResetAcknowledged} onChange={(event) => setValidationResetAcknowledged(event.target.checked)} disabled={isAnalyzing} />
              <span>기존 검수 결과와 이슈 기록이 초기화되며, 모든 문항이 검수 대기로 전환됨을 이해했습니다.</span>
            </label>
            <label className="question-validation-modal__field">
              <span>실행하려면 <strong>전체 검수 대기</strong>를 입력하세요.</span>
              <input className="admin-form-control" value={validationResetConfirmationText} onChange={(event) => setValidationResetConfirmationText(event.target.value)} disabled={isAnalyzing} />
            </label>
            {validationResetError && <p className="question-validation-modal__error" role="alert">{validationResetError}</p>}
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setIsValidationResetModalOpen(false)} disabled={isAnalyzing}>취소</button>
              <button className="admin-btn danger" onClick={resetAllValidationStates} disabled={!validationResetAcknowledged || validationResetConfirmationText !== '전체 검수 대기' || isAnalyzing}>
                {isAnalyzing ? '초기화 중...' : '모든 문항 검수 대기 전환'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isValidationModalOpen && (
        <div className="question-action-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="batch-validation-title">
          <div className="question-action-modal question-validation-modal">
            <h3 id="batch-validation-title">문제 일괄 검증 실행</h3>
            <p className="question-validation-modal__warning">
              이 작업은 검수 대기 문항만 {validationLimit === 0 ? '전체' : `최대 ${validationLimit}개`} AI로 검토합니다. 문제 지문·선택지·정답·해설은 자동 수정하지 않지만, 검수 상태와 검토 이슈가 운영 DynamoDB에 저장됩니다.
            </p>
            {validationPreview ? (
              <p className="question-validation-modal__summary">전체 {validationPreview.total.toLocaleString()}개를 규칙 스캔한 결과, AI 검증 대상은 {validationPreview.pending.toLocaleString()}개입니다.</p>
            ) : !validationError && <p>전체 문항을 규칙 기반으로 사전 스캔하고 있습니다…</p>}
            <label className="question-validation-modal__field">
              <span>이번 실행 수</span>
              <select className="admin-form-control" value={validationLimit} onChange={(event) => setValidationLimit(Number(event.target.value))} disabled={isAnalyzing}>
                <option value={0}>전체 (제한 없음)</option>
                <option value={25}>25개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
              </select>
            </label>
            <label className="question-validation-modal__check">
              <input type="checkbox" checked={validationAcknowledged} onChange={(event) => setValidationAcknowledged(event.target.checked)} disabled={isAnalyzing} />
              <span>검수 결과가 운영 데이터에 기록되며, 검토 필요 문항을 제가 확인해야 함을 이해했습니다.</span>
            </label>
            <label className="question-validation-modal__field">
              <span>실행하려면 <strong>검수 시작</strong>을 입력하세요.</span>
              <input className="admin-form-control" value={validationConfirmationText} onChange={(event) => setValidationConfirmationText(event.target.value)} disabled={isAnalyzing} />
            </label>
            {validationError && <p className="question-validation-modal__error" role="alert">{validationError}</p>}
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setIsValidationModalOpen(false)} disabled={isAnalyzing}>취소</button>
              <button className="admin-btn warning" onClick={startBatchValidation} disabled={!validationPreview || validationPreview.pending === 0 || !validationAcknowledged || validationConfirmationText !== '검수 시작' || isAnalyzing}>
                {isAnalyzing ? '검증 실행 중...' : '검수 대기 문제 검증'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteQuestionId && (
        <div className="question-action-modal-overlay">
          <div className="question-action-modal">
            <h3>문제 삭제</h3>
            <p>정말 이 문제를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.</p>
            <div className="question-action-modal__footer">
              <button className="admin-btn secondary" onClick={() => setPendingDeleteQuestionId(null)}>취소</button>
              <button
                className="admin-btn danger"
                onClick={async () => {
                  await deleteQuestion(pendingDeleteQuestionId);
                  setPendingDeleteQuestionId(null);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const QuestionModal: React.FC<{
  title: string;
  question: Question;
  domains: ExamDomain[];
  error: string;
  onChange: (question: Question) => void;
  onClose: () => void;
  onSave: () => void;
  onRestore?: (revision: QuestionRevision) => void;
  quality?: React.ReactNode;
}> = ({ title, question, domains, error, onChange, onClose, onSave, onRestore, quality }) => (
  <div className="question-edit-modal-overlay">
    <div className="question-edit-modal">
      <div className="question-edit-modal__header">
        <h3>{title}</h3>
        <button className="question-edit-modal__close" onClick={onClose} aria-label="닫기">닫기</button>
      </div>
      <div className="question-edit-modal__body">
        {quality}
        <QuestionEditor question={question} domains={domains} onChange={onChange} />
        {error && <p className="question-edit-modal__error">{error}</p>}
        <QuestionReviewInfo question={question} onRestore={onRestore} />
      </div>
      <div className="question-edit-modal__footer">
        <button className="admin-btn primary" onClick={onSave}>저장</button>
        <button className="admin-btn secondary" onClick={onClose}>취소</button>
      </div>
    </div>
  </div>
);

/** 수정 화면 하단: 검수 이슈, 승인 기록, 수정 이력(되돌리기) */
const QuestionReviewInfo: React.FC<{
  question: Question;
  onRestore?: (revision: QuestionRevision) => void;
}> = ({ question, onRestore }) => {
  const issues = question.validation_issues || [];
  const revisions = [...(question.revisions || [])].sort((left, right) => right.at.localeCompare(left.at));
  if (!question.question_id || (issues.length === 0 && !question.approved_at && revisions.length === 0)) return null;

  return (
    <div className="qbank-review">
      {issues.length > 0 && (
        <div className="qbank-review__block">
          <h4>검수 이슈</h4>
          <ul className="qbank-review__issues">
            {issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`} className={`qbank-review__issue qbank-review__issue--${issue.severity}`}>
                <span className="qbank-review__code">{issue.severity === 'error' ? '오류' : '확인'}</span>
                {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {question.approved_at && (
        <p className="qbank-review__approved">
          {formatDateTime(question.approved_at)} · {question.approved_by || '관리자'} 승인
        </p>
      )}

      {revisions.length > 0 && (
        <details className="qbank-revisions">
          <summary>수정 이력 {revisions.length}건</summary>
          <ol>
            {revisions.map((revision) => {
              const fields = Object.keys(revision.before);
              return (
                <li key={revision.at} className="qbank-revisions__item">
                  <div className="qbank-revisions__head">
                    <span className="tabular">{formatDateTime(revision.at)}</span>
                    <span className="qbank-tag">{REVISION_SOURCE_LABELS[revision.source] || revision.source}</span>
                    {revision.by && <span className="qbank-revisions__by">{revision.by}</span>}
                    {onRestore && fields.length > 0 && (
                      <button type="button" className="admin-btn sm secondary" onClick={() => onRestore(revision)}>
                        변경 전으로 되돌리기
                      </button>
                    )}
                  </div>
                  <p className="qbank-revisions__fields">
                    바뀐 항목: {fields.map((field) => REVISION_FIELD_LABELS[field] || field).join(', ') || '-'}
                    {revision.before.correct_answers && ` · 이전 정답 ${revision.before.correct_answers.join(', ')}`}
                  </p>
                  {revision.before.question_text && (
                    <p className="qbank-revisions__preview">이전 문제: {revision.before.question_text.slice(0, 140)}{revision.before.question_text.length > 140 ? '…' : ''}</p>
                  )}
                </li>
              );
            })}
          </ol>
        </details>
      )}
    </div>
  );
};

const QuestionEditor: React.FC<{
  question: Question;
  domains: ExamDomain[];
  onChange: (question: Question) => void;
}> = ({ question, domains, onChange }) => {
  const updateQuestion = (updates: Partial<Question>) => {
    onChange({ ...question, ...updates });
  };

  const updateOption = (index: number, text: string) => {
    const newOptions = [...question.options];
    newOptions[index] = { ...newOptions[index], option_text: text };
    updateQuestion({ options: newOptions });
  };

  const updateOptionExplanation = (index: number, text: string) => {
    const newOptions = [...question.options];
    newOptions[index] = { ...newOptions[index], explanation: text };
    updateQuestion({ options: newOptions });
  };

  const [newReference, setNewReference] = React.useState('');
  const addReference = () => {
    const url = newReference.trim();
    if (!/^https?:\/\//.test(url) || (question.references || []).some((item) => item.url === url)) return;
    updateQuestion({ references: [...(question.references || []), { url }] });
    setNewReference('');
  };

  const addOption = () => {
    if (question.options.length < 6) {
      updateQuestion({
        options: [...question.options, { option_number: question.options.length + 1, option_text: '' }],
      });
    }
  };

  const removeOption = (index: number) => {
    if (question.options.length <= 2) return;

    const reorderedOptions = question.options
      .filter((_, itemIndex) => itemIndex !== index)
      .map((option, itemIndex) => ({ ...option, option_number: itemIndex + 1 }));

    updateQuestion({
      options: reorderedOptions,
      correct_answers: question.correct_answers.filter(answer => answer <= reorderedOptions.length),
    });
  };

  const toggleCorrectAnswer = (optionNumber: number) => {
    const currentAnswers = normalizeCorrectAnswers(question.correct_answers);
    const isCorrect = currentAnswers.includes(optionNumber);
    updateQuestion({
      correct_answers: isCorrect
        ? currentAnswers.filter(answer => answer !== optionNumber)
        : [...currentAnswers, optionNumber].sort(),
    });
  };

  return (
    <div className="question-editor">
      <div className="form-group">
        <label>문제 내용</label>
        <textarea
          className="admin-form-control"
          rows={2}
          value={question.question_text}
          onChange={(event) => updateQuestion({ question_text: event.target.value })}
          placeholder="문제 내용을 입력하세요."
        />
      </div>

      {domains.length > 0 && (
        <div className="qbank-editor-meta">
          <div className="form-group">
            <label htmlFor="question-domain">
              출제 영역
              {question.domain_source === 'ai' && <span className="qbank-tag qbank-tag--draft">AI 초안</span>}
            </label>
            <select
              id="question-domain"
              className="admin-form-control"
              value={question.domain || ''}
              onChange={(event) => updateQuestion({ domain: event.target.value || null, domain_source: 'manual' })}
            >
              <option value="">미분류</option>
              {domains.map((domain) => (
                <option key={domain.id} value={domain.id}>{domain.name} ({domain.weight}%)</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="question-difficulty">난이도</label>
            <select
              id="question-difficulty"
              className="admin-form-control"
              value={question.difficulty || ''}
              onChange={(event) => updateQuestion({
                difficulty: event.target.value ? (Number(event.target.value) as 1 | 2 | 3) : null,
                domain_source: 'manual',
              })}
            >
              <option value="">미지정</option>
              {[1, 2, 3].map((level) => (
                <option key={level} value={level}>{DIFFICULTY_LABELS[level]}</option>
              ))}
            </select>
          </div>
          {question.domain_source === 'ai' && question.domain && (
            <button type="button" className="admin-btn sm secondary qbank-editor-meta__confirm" onClick={() => updateQuestion({ domain_source: 'manual' })}>
              AI 분류 확정
            </button>
          )}
        </div>
      )}
      {domains.length === 0 && (
        <p className="qbank-editor-hint">이 시험 유형은 출제 영역 정의가 없어 영역·난이도를 고를 수 없습니다. (backend/src/config/examDomains.ts)</p>
      )}

      <div className="form-group">
        <label>선택지 · 보기별 해설</label>
        <p className="qbank-editor-hint">체크한 보기가 정답입니다. 보기 해설에는 번호나 O/X 없이 그 보기가 왜 맞거나 틀린지만 쓰세요. 보기 순서는 학생마다 섞이고, 해설은 보기를 따라갑니다.</p>
        <div className="options-editor">
          {question.options.map((option, index) => (
            <div key={option.option_number} className="option-item">
              <div className="option-header">
                <label>
                  <input
                    type="checkbox"
                    checked={normalizeCorrectAnswers(question.correct_answers).includes(option.option_number)}
                    onChange={() => toggleCorrectAnswer(option.option_number)}
                  />
                  선택지 {option.option_number}
                </label>
                {question.options.length > 2 && (
                  <button type="button" className="admin-btn danger sm" onClick={() => removeOption(index)}>
                    삭제
                  </button>
                )}
              </div>
              <textarea
                className="admin-form-control"
                rows={1}
                value={option.option_text}
                onChange={(event) => updateOption(index, event.target.value)}
                placeholder={`선택지 ${option.option_number} 내용`}
              />
              <textarea
                className={`admin-form-control qbank-option-explanation ${normalizeCorrectAnswers(question.correct_answers).includes(option.option_number) ? 'is-correct' : ''}`}
                rows={2}
                value={option.explanation || ''}
                onChange={(event) => updateOptionExplanation(index, event.target.value)}
                placeholder={normalizeCorrectAnswers(question.correct_answers).includes(option.option_number) ? '이 보기가 정답인 이유' : '이 보기가 오답인 이유'}
                aria-label={`선택지 ${option.option_number} 해설`}
              />
            </div>
          ))}
          {question.options.length < 6 && (
            <button type="button" className="admin-btn secondary" onClick={addOption}>
              선택지 추가
            </button>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>공통 해설</label>
        <textarea
          className="admin-form-control"
          rows={3}
          value={question.explanation}
          onChange={(event) => updateQuestion({ explanation: event.target.value })}
          placeholder="모든 보기에 공통인 설명. '2번은…'처럼 보기 번호 대신 보기 내용으로 쓰세요. URL은 아래 참고 자료에."
        />
      </div>

      {question.reasoning?.trim() && (
        <div className="form-group">
          <label>구 형식 근거 (보기별로 나눌 수 없는 형식)</label>
          <p className="qbank-editor-hint">위 보기별 해설 칸으로 옮겨 적은 뒤 이 칸을 비우고 저장하세요. AI 품질 검토를 돌리면 자동으로 나눈 수정안을 만들어 줍니다.</p>
          <textarea
            className="admin-form-control"
            rows={3}
            value={question.reasoning}
            onChange={(event) => updateQuestion({ reasoning: event.target.value })}
          />
        </div>
      )}

      <div className="form-group">
        <label>참고 자료</label>
        <ul className="qbank-references">
          {(question.references || []).map((reference) => (
            <li key={reference.url}>
              <a href={reference.url} target="_blank" rel="noreferrer">{reference.title || reference.url}</a>
              <button
                type="button"
                className="admin-btn sm secondary"
                onClick={() => updateQuestion({ references: (question.references || []).filter((item) => item.url !== reference.url) })}
              >
                삭제
              </button>
            </li>
          ))}
          {(question.references || []).length === 0 && <li className="qbank-references__empty">참고 자료가 없습니다.</li>}
        </ul>
        <div className="qbank-references__add">
          <input
            type="url"
            className="admin-form-control"
            value={newReference}
            onChange={(event) => setNewReference(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addReference(); } }}
            placeholder="https://docs.aws.amazon.com/..."
          />
          <button type="button" className="admin-btn sm secondary" onClick={addReference}>추가</button>
        </div>
      </div>

      <div className="form-group">
        <label>키워드</label>
        <div className="keywords-input">
          <div className="keywords-list">
            {(question.keywords || []).map((keyword, index) => (
              <span key={keyword} className="keyword-tag">
                {keyword}
                <button
                  type="button"
                  onClick={() => {
                    const newKeywords = [...(question.keywords || [])];
                    newKeywords.splice(index, 1);
                    updateQuestion({ keywords: newKeywords });
                  }}
                >
                  삭제
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            placeholder="키워드 입력 후 Enter"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const keyword = event.currentTarget.value.trim();
                if (keyword && !(question.keywords || []).includes(keyword)) {
                  updateQuestion({ keywords: [...(question.keywords || []), keyword] });
                  event.currentTarget.value = '';
                }
              }
            }}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="admin-checkbox-label">
          <input
            type="checkbox"
            checked={question.validated || false}
            onChange={(event) => updateQuestion({ validated: event.target.checked })}
          />
          승인 (시험에 출제)
        </label>
      </div>
    </div>
  );
};

export default QuestionManagementPage;
